// src/data/pulseClient.js
//
// PULSE-LOAD-PERF: Per-section client functions for the Pulse landing page.
// Each function hits its own focused endpoint so PulsePage can fire all
// six in parallel via Promise.allSettled and render each section as soon
// as its own data lands.
//
// PULSE-LAYOUT-RESTRUCTURE: All get* functions accept opts = { includeServiced: bool }
// and append ?includeServiced=0 to the URL when false, so the server can filter
// serviced_office rows from every endpoint.

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

async function apiFetch(url) {
  let res;
  try {
    res = await fetch(url, { credentials: "include" });
  } catch (err) {
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("pulseClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

function svcQs(opts) {
  const params = [];
  if (opts && opts.includeServiced === false) params.push("includeServiced=0");
  if (opts && opts.locationId) params.push(`locationId=${encodeURIComponent(opts.locationId)}`);
  return params.length > 0 ? `?${params.join("&")}` : "";
}

/**
 * getPulseKpi(opts?)
 * GET /api/pulse/kpi
 * Returns: { kpi: { locationCount, sqftTotal, seatsTotal, headcountTotal,
 *   annualOccupancy, annualBaseRent } }. annualOccupancy is a compatibility
 *   alias for net current-year USD rent; annualBaseRent carries provenance.
 */
export async function getPulseKpi(opts = {}) {
  return apiFetch(`/api/pulse/kpi${svcQs(opts)}`);
}

/** Home annual USD run rate: { totalUsd: number|null, status: 'complete'|'partial'|'unavailable', coverage }. */
export async function getPulseAnnualPortfolioCost() {
  return apiFetch("/api/pulse/annual-portfolio-cost");
}

/**
 * getPulseNeedsAttention(opts?)
 * GET /api/pulse/needs-attention
 * Returns: { whatsAhead, needsAttention }
 */
export async function getPulseNeedsAttention(opts = {}) {
  return apiFetch(`/api/pulse/needs-attention${svcQs(opts)}`);
}

/**
 * getPulseLeaseDecisions(opts?)
 * GET /api/pulse/lease-decisions
 * Returns: { leaseDecisions }
 */
export async function getPulseLeaseDecisions(opts = {}) {
  return apiFetch(`/api/pulse/lease-decisions${svcQs(opts)}`);
}

/**
 * getPulseUtilization(opts?)
 * GET /api/pulse/utilization
 * Returns: { utilization: { rows, hasData } }
 */
export async function getPulseUtilization(opts = {}) {
  return apiFetch(`/api/pulse/utilization${svcQs(opts)}`);
}

/**
 * getPulseRead(opts?)
 * GET /api/pulse/read
 * Returns: { narrative: string | null, chips: string[], computedAt: string | null,
 *   annualBaseRent?: { annualRentUsd, grossAnnualRentUsd, subleaseRecoveryUsd,
 *   recoveryCoverage, fxBasis, fxRatesUsed, explanation } }
 * narrative is null until the daily pulse-read job has run at least once.
 */
export async function getPulseRead(opts = {}) {
  return apiFetch(`/api/pulse/read${svcQs(opts)}`);
}

/**
 * getPulseLeaseExpirations(opts?)
 * GET /api/pulse/lease-expirations
 * Returns: { leaseExpirationsByYear: Array<{ year: number, count: number, bucket: { color: string, label: string } }> }
 * Only includes current year forward; historical years are excluded server-side.
 */
export async function getPulseLeaseExpirations(opts = {}) {
  return apiFetch(`/api/pulse/lease-expirations${svcQs(opts)}`);
}

/**
 * getPulseNextDecisions(opts?)
 * GET /api/pulse/next-decisions
 * Returns: { nextDecisions: Array<{ id, type, typeLabel, locationName, locationAlias,
 *   dueDate, daysUntil, dueLabel, priority, stakeUsd, stakeLabel, stakeSense }> }
 */
export async function getPulseNextDecisions(opts = {}) {
  return apiFetch(`/api/pulse/next-decisions${svcQs(opts)}`);
}

/**
 * getPulseUtilizationTrend(opts?)
 * GET /api/pulse/utilization-trend
 * opts.locationId (optional) scopes the aggregation to a single office
 * instead of summing across the portfolio.
 * Returns: { months: Array<{ monthDate, monthKey, monthLabel, avgPct, peakPct }>,
 *   currentMonth: { monthDate, monthKey, monthLabel, avgPct, peakPct } | null,
 *   hasData: boolean }
 */
export async function getPulseUtilizationTrend(opts = {}) {
  return apiFetch(`/api/pulse/utilization-trend${svcQs(opts)}`);
}

/**
 * getPulseAttendance(opts?)
 * GET /api/pulse/attendance
 * Returns aggregate-only Attendance pane data for all supported scopes.
 */
export async function getPulseAttendance(opts = {}) {
  const params = new URLSearchParams();
  if (opts.scopeType && opts.scopeType !== "all") params.set("scopeType", opts.scopeType);
  if (opts.scopeValue) params.set("scopeValue", opts.scopeValue);
  if (opts.weekStart) params.set("weekStart", opts.weekStart);
  const query = params.toString();
  return apiFetch(`/api/pulse/attendance${query ? `?${query}` : ""}`);
}

/**
 * getPulseCostTrajectory(opts?)
 * GET /api/pulse/cost-trajectory
 * Returns: { trajectory: Array<{ year, rentTotal, wpTotal, secTotal, opexTotal, isProjected }>,
 *            sqftTotal, seatsTotal, attendedTotal, fxBasis, fxRatesUsed, explanation }
 * rentTotal is USD and may be null; each year carries missingCount and partial.
 */
export async function getPulseCostTrajectory(opts = {}) {
  return apiFetch(`/api/pulse/cost-trajectory${svcQs(opts)}`);
}

/**
 * getPulseLeaseCliffs(opts?)
 * GET /api/pulse/lease-cliffs
 * Returns: { headline: { year, rentTotal } | null, cliffs: Array<{ year, rentTotal, leaseCount, isCliff }> }
 * Includes fxBasis, fxRatesUsed, explanation and per-year missingCount,
 * estimatedCount, partial. rentTotal is USD and may be null.
 */
export async function getPulseLeaseCliffs(opts = {}) {
  return apiFetch(`/api/pulse/lease-cliffs${svcQs(opts)}`);
}

/**
 * getPulseAiPaneRead(pane)
 * GET /api/pulse/pane-read/:pane
 * Returns: { text: string | null }
 * text is null when the ask_portfolio flag is off, data is unavailable,
 * or any LLM error occurs.  Never throws — always resolves to { text }.
 *
 * @param {string} pane - One of: portfolio-health | location-health |
 *   cost-trajectory | lease-cliffs | space-headroom | next-decisions |
 *   utilization-trend
 */
export async function getPulseAiPaneRead(pane, opts = {}) {
  const qs = opts.includeServiced === false ? "?includeServiced=0" : "";
  try {
    return await apiFetch(`/api/pulse/pane-read/${encodeURIComponent(pane)}${qs}`);
  } catch {
    return { text: null };
  }
}

/**
 * getPulseLocationRisk(opts?)
 * GET /api/pulse/location-risk
 * Returns: { cards, dismissedCards }
 *   cards:         Array of active In Focus cards (not dismissed/snoozed for this user).
 *   dismissedCards: Array of dismissed/snoozed cards for this user (each has
 *                  dismissedAction:'acknowledged'|'snoozed' and snoozedUntil:string|null).
 * Each card shape: { nm, sm, issue, urgency, cost, blockers, own, band, cat, due, dueState,
 *                   imageUrl, cardKey, locationId, ... }
 * Never throws — resolves to { cards: [], dismissedCards: [] } on any network or server error.
 */
export async function getPulseLocationRisk(opts = {}) {
  try {
    return await apiFetch(`/api/pulse/location-risk${svcQs(opts)}`);
  } catch {
    return { cards: [], dismissedCards: [] };
  }
}

/**
 * acknowledgeInFocusCard(cardKey)
 * POST /api/pulse/infocus-dismissals { cardKey, action: 'acknowledged' }
 * Permanently dismisses an In Focus card for the current user until they restore it.
 */
export async function acknowledgeInFocusCard(cardKey) {
  const res = await fetch("/api/pulse/infocus-dismissals", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cardKey, action: "acknowledged" }),
  });
  if (!res.ok) throw new Error(`acknowledgeInFocusCard failed: ${res.status}`);
  return res.json();
}

/**
 * snoozeInFocusCard(cardKey, snoozeUntil)
 * POST /api/pulse/infocus-dismissals { cardKey, action: 'snoozed', snoozeUntil }
 * Hides the card until snoozeUntil ('YYYY-MM-DD'), then it reappears automatically.
 */
export async function snoozeInFocusCard(cardKey, snoozeUntil) {
  const res = await fetch("/api/pulse/infocus-dismissals", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cardKey, action: "snoozed", snoozeUntil }),
  });
  if (!res.ok) throw new Error(`snoozeInFocusCard failed: ${res.status}`);
  return res.json();
}

/**
 * restoreInFocusCard(cardKey)
 * DELETE /api/pulse/infocus-dismissals/:cardKey
 * Removes a dismissal so the card reappears in the active In Focus list.
 */
export async function restoreInFocusCard(cardKey) {
  const res = await fetch(`/api/pulse/infocus-dismissals/${encodeURIComponent(cardKey)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`restoreInFocusCard failed: ${res.status}`);
  return res.json();
}

/**
 * getPulseLeaseExposure(basis?)
 * GET /api/pulse/lease-exposure
 * @param {{ scope?: 'fully_loaded'|'base', rate?: 'effective'|'contractual',
 *   scopeType?: 'all'|'region'|'market'|'office', scopeValue?: string,
 *   locationId?: string, includeDetail?: boolean }} basis
 * Returns: { exposure: { basis, totals, leaseCount, lastExpiryYear, horizon,
 *   byYear, decisionWindow: { months, totalUsd, count, leaseIds },
 *   strategySplit, markets, topLeases } }
 * Never throws — server gates by role and returns no payload for unpermitted
 * users; this function surfaces that as a rejected promise for the caller
 * to catch (PulsePage treats a rejected fetch as "hide the pane").
 */
export async function getPulseLeaseExposure(basis = {}) {
  const scope = basis.scope === "base" ? "base" : "fully_loaded";
  const rate  = basis.rate === "contractual" ? "contractual" : "effective";
  // Preserve the original single-office URL for older callers. New callers
  // use the explicit scopeType/scopeValue pair below.
  if (basis.locationId && !basis.scopeType && !basis.scopeValue) {
    const detailParam = basis.includeDetail ? "&includeDetail=1" : "";
    return apiFetch(`/api/pulse/lease-exposure?scope=${scope}&rate=${rate}&locationId=${encodeURIComponent(basis.locationId)}${detailParam}`);
  }
  const scopeType = ["all", "region", "market", "office"].includes(basis.scopeType)
    ? basis.scopeType
    : (basis.locationId ? "office" : "all");
  const scopeValue = scopeType === "all"
    ? null
    : (basis.scopeValue ?? basis.locationId);
  const locationScopeParams = scopeType !== "all"
    ? `&scopeType=${encodeURIComponent(scopeType)}&scopeValue=${encodeURIComponent(scopeValue ?? "")}`
    : "";
  const detailParam = basis.includeDetail ? "&includeDetail=1" : "";
  return apiFetch(`/api/pulse/lease-exposure?scope=${scope}&rate=${rate}${locationScopeParams}${detailParam}`);
}

/**
 * getPulseDataConfidence()
 * GET /api/pulse/data-confidence
 * Returns: { domains: Array<{ id, label, lastUpdated, confidence, isMock }> }
 * confidence: "High" | "Med" | "Low"
 * isMock: true for Cost·Finance, People-EX·Survey, Intel·Slack-JLL until real ingestion is wired.
 */
export async function getPulseDataConfidence() {
  return apiFetch("/api/pulse/data-confidence");
}

/**
 * getPulseCategoryImages()
 * GET /api/pulse/category-images
 * Returns a plain object mapping category name → most-recently-added active photo URL,
 * e.g. { Decision: "https://...", Insight: "https://..." }.
 * Categories with no active photo are omitted from the map.
 * Never throws — resolves to {} on any network or server error.
 */
export async function getPulseCategoryImages() {
  try {
    return await apiFetch("/api/pulse/category-images");
  } catch {
    return {};
  }
}

/**
 * getPulseSummary()
 * GET /api/pulse/summary  (backward-compatible thin wrapper)
 * Returns the original monolithic payload shape.
 * Prefer the six focused functions above for new code.
 *
 * @deprecated Use the six per-section functions instead.
 */
export async function getPulseSummary() {
  return apiFetch("/api/pulse/summary");
}
